import set from "lodash/set";
import get from "lodash/get";
import debounce from "lodash/debounce";
import {Ref, ref, watch, provide} from "vue";
import { z } from "zod";

export const ERROR_ANCHOR_CLASS = 'form-validation-input-error';
export const FORM_BLUR_EVENT = 'input-blurred';

export class FieldValidationResult {
    constructor(
        public invalid = false,
        public message = '',
        public submitAttempted = false
    ) {}
}

export type SchemaValidationResult<T> = {
    [P in keyof T]-?: SchemaValidationResult<T[P]> & FieldValidationResult;
}

export type FormValidationTrigger = {
    validate: (submitAttemptedVal?: boolean) => boolean;
}

export type FormValidationProvider = FormValidationTrigger & {
    addFormValidator: (validator: Ref<FormValidationTrigger>) => void;
    removeFormValidator: (validator: Ref<FormValidationTrigger>) => void;
}

export type FormValidator<T> = FormValidationTrigger & {
    result: Ref<SchemaValidationResult<T>>;
    submitAttempted: Ref<boolean>;
}
    
export const FormValidationProviderKey = Symbol('FormValidationProviderKey');

export function useFormValidationProvider() : FormValidationProvider {
    const validators = ref<(Ref<FormValidationTrigger>)[]>([]);
    const validationProvider = {
        validate,
        addFormValidator,
        removeFormValidator
    };

    provide(FormValidationProviderKey, validationProvider);

    return validationProvider;

    function addFormValidator(validator: Ref<FormValidationTrigger>): void {
        validators.value = [...validators.value, validator];
    }

    function removeFormValidator(validator: Ref<FormValidationTrigger>): void {
        const index = validators.value.indexOf(validator);
        if (index >= 0)
            validators.value.splice(index, 1);
    }

    function validate(submitAttemptedVal = true): boolean {
        return validators.value.filter(x => !x.value.validate(submitAttemptedVal)).length === 0;
    }
}
    
export function useFormValidation<T>(schema: z.ZodTypeAny, model: Ref<T>, attachBlurEventListener = true) : FormValidator<T> {

    const cachedInitialResult = getInitialValidationResult<T>(schema);
    const result = ref(getInitialValidationResult<T>(schema)) as Ref<SchemaValidationResult<T>>;
    const submitAttempted = ref(false);

    if (attachBlurEventListener)
        addEventListener(FORM_BLUR_EVENT, () => validate(false));

    watch(model, debounce(() => validate(false), 500, { leading: true }), { deep: true });

    return { 
        submitAttempted, 
        result: result, 
        validate
    };

    function validate(submitAttemptedVal = true): boolean {
        if (submitAttemptedVal) submitAttempted.value = true;

        const parsed = schema.safeParse(model.value);
        const newValidationState = structuredClone(cachedInitialResult);
        
        if (!parsed.success) {
            parsed.error.issues.forEach(x => {
                // Preserves state if not overwritten on success.  Allows parents to have FieldValidationResults in addition to children properties.
                const newIssue = {
                    ...get(newValidationState, x.path.join('.'), {}),
                    ...new FieldValidationResult(true, x.message, submitAttempted.value)
                };
                set(newValidationState, x.path.join('.'), newIssue);
            });
        }

        result.value = newValidationState;
        return parsed.success;
    }
}

export function getInitialValidationResult<T>(schema: z.ZodTypeAny): SchemaValidationResult<T> {
    const validationState = {} as SchemaValidationResult<T>;
    getZodKeys(schema).forEach(x => {
        if (x.endsWith('[]'))
            set(validationState, x.replace('.[]', ''), []); // Map ZodArrays to empty arrays
        else
            set(validationState, x, new FieldValidationResult()); // Otherwise, map child properties
    })
    return validationState;
}

// Get zod object keys recursively. Used to build validation states that match models' structure
// See: https://github.com/colinhacks/zod/discussions/2134
function getZodKeys<T extends z.ZodTypeAny>(schema: T): string[] {
    if (schema === null || schema === undefined) return [];
    if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional) return getZodKeys(schema.unwrap());
    if (schema instanceof z.ZodArray) {
        return ['[]']; // Used to initialize ZodArrays as empty arrays for validation results.
    }
    if (schema instanceof z.ZodEffects) { // To make compatible with schemas with .refine or .superRefine applied
        return getZodKeys(schema._def?.schema ?? schema);
    }
    if (schema instanceof z.ZodObject) {
        const entries = Object.entries(schema.shape);
        return entries.flatMap(([key, value]) => {
            const nested = value instanceof z.ZodType ? getZodKeys(value).map(subKey => `${key}.${subKey}`) : [];
            return nested.length ? nested : key;
        });
    }
    return [];
}
